import React from 'react';
import i18n from '@/i18n/config';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
  Textarea
} from '@heroui/react';
import { useAtom } from 'jotai';
import { toast } from 'react-toastify';

import { updateOrganizationAtom } from '@/store';

interface EditOrganizationModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  organization: {
    id: number;
    name: string;
    desc: string | null;
    status: string;
    ai_daily_request_limit?: number | null;
  } | null;
  onSuccess?: () => void;
}

export function EditOrganizationModal({ isOpen, onOpenChange, organization, onSuccess }: EditOrganizationModalProps) {
  const [, updateOrgAtom] = useAtom(updateOrganizationAtom);
  const [formData, setFormData] = React.useState({
    name: '',
    desc: '',
    status: 'active',
    ai_daily_request_limit: '' as string
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Initialize form data when organization changes
  React.useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name,
        desc: organization.desc || '',
        status: organization.status,
        ai_daily_request_limit:
          organization.ai_daily_request_limit === null || organization.ai_daily_request_limit === undefined
            ? ''
            : String(organization.ai_daily_request_limit)
      });
    }
  }, [organization]);

  const handleSubmit = React.useCallback(async () => {
    if (!organization) return;

    if (!formData.name.trim()) {
      toast.error(i18n.t('copy.workspace_name_is_required'));
      return;
    }

    try {
      setIsSubmitting(true);
      await updateOrgAtom({
        id: organization.id,
        formData: {
          ...formData,
          ai_daily_request_limit:
            formData.ai_daily_request_limit.trim() === ''
              ? null
              : Number(formData.ai_daily_request_limit)
        }
      });
      toast.success(i18n.t('copy.workspace_updated_successfully'));
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(i18n.t('copy.failed_to_update_workspace'));
    } finally {
      setIsSubmitting(false);
    }
  }, [organization, formData, updateOrganizationAtom, onSuccess, onOpenChange]);

  const handleClose = React.useCallback(() => {
    setFormData({
      name: '',
      desc: '',
      status: 'active',
      ai_daily_request_limit: ''
    });
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Edit Workspace</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <Input
                  label={i18n.t('copy.workspace_name')}
                  placeholder={i18n.t('copy.enter_workspace_name')}
                  value={formData.name}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, name: value }))}
                  isRequired
                  autoFocus
                />

                <Textarea
                  label={i18n.t('copy.description')}
                  placeholder={i18n.t('copy.enter_workspace_description')}
                  value={formData.desc}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, desc: value }))}
                  minRows={3}
                />

                <Input
                  description="Leave empty for unlimited or global default"
                  label={i18n.t('copy.ai_daily_request_limit')}
                  placeholder={i18n.t('copy.e_g_100')}
                  type="number"
                  value={formData.ai_daily_request_limit}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, ai_daily_request_limit: value }))}
                />

                <Switch
                  isSelected={formData.status === 'active'}
                  onValueChange={(isSelected) => {
                    setFormData((prev) => ({ ...prev, status: isSelected ? 'active' : 'inactive' }));
                  }}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{i18n.t('filters.status')}</span>
                    <span className="text-default-400 text-xs">
                      {formData.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </Switch>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button color="primary" isLoading={isSubmitting} onPress={handleSubmit}>{i18n.t('common.update')}</Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
